import { EntityInfo } from '@record-it-npm/synchro-common';
import { RateLimit } from 'record-it-utils/utils/RateLimit/RateLimit';
import { EntityManagerBaseEntity } from '../EntityManagerBaseEntity';
import { EntityManagerEntityTypesByEntityName } from '../EntityManagerEntityTypesByEntityName';
import { StorageAdapter, StorageAdapterEntityName } from '../StorageAdapter/StorageAdapter';
import { AnySynchronizationEnvironmentTypesConstraint } from '../SynchronizationEnvironmentTypes';
import { Operation, OperationExecutor, OperationType } from './OperationExecutor';
export class EntityStorage<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> {
  private static initializedStorage: EntityStorage<any, any> | null = null;
  private initialized: boolean = false;
  private pendingOperations: OperationsByEntityName<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName> = new Map();
  private flushRateLimited = RateLimit.rateLimitFunction(this.flush.bind(this), 20);
  private executor: OperationExecutor<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName> = new OperationExecutor();
  public async init(): Promise<void> {
    if (this.initialized) {
      return;
    }
    if (EntityStorage.initializedStorage) {
      // because they would generate the same ids since they are not synchronized
      throw new Error('only one EntityStorage is allowed to be initialized');
    }
    EntityStorage.initializedStorage = this;
    this.initialized = true;
  }
  public destroy(): void {
    if (this.pendingOperations.size) {
      throw new Error("can't destroy the EntityStorage when it still has pending operations");
    }
    EntityStorage.initializedStorage = null;
  }
  public saveEntity<T extends EntityInfo<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'], any, any>>(entityInfo: T, entity: TEntityTypesByEntityName[T['entityName']]['entity']): void {
    this.assertInitialized();
    this.queueOperation({
      type: OperationType.SAVE,
      entity,
      entityId: entity.id,
      entityName: entityInfo.entityName
    });
    this.flushRateLimited();
  }
  public deleteEntity(entityInfo: EntityInfo<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'], any, any>, entityId: EntityManagerBaseEntity['id']): void {
    this.assertInitialized();
    this.queueOperation({
      type: OperationType.DELETE,
      entityId,
      entityName: entityInfo.entityName
    });
    this.flushRateLimited();
  }
  public async loadEntities<TSpecificEntityName extends TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']>(entityInfo: EntityInfo<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes'], TSpecificEntityName, TEntityTypesByEntityName[TSpecificEntityName]['entity']>): Promise<Array<TEntityTypesByEntityName[TSpecificEntityName]['entity']>> {
    return StorageAdapter.getEntities(entityInfo.entityName as StorageAdapterEntityName);
  }
  public async flush(): Promise<void> {
    const operations = this.getAllPendingOperationsAsArray();
    this.pendingOperations = new Map();
    const failedOperations = await this.executor.executeOperations(operations);
    this.queueLowPriorityOperations(failedOperations);
  }
  private queueLowPriorityOperations(operations: Array<Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>): void {
    for (const operation of operations) {
      const operationByEntityId = this.getOrCreateOperationByEntityId(operation.entityName);
      if (!operationByEntityId.has(operation.entityId)) {
        operationByEntityId.set(operation.entityId, operation);
      }
    }
  }
  private queueOperation(operation: Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>): void {
    const operationByEntityId = this.getOrCreateOperationByEntityId(operation.entityName);
    operationByEntityId.set(operation.entityId, operation);
  }
  private getOrCreateOperationByEntityId(entityName: TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName']): OperationByEntityId<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName> {
    let operationByEntityId = this.pendingOperations.get(entityName);
    if (!operationByEntityId) {
      operationByEntityId = new Map();
      this.pendingOperations.set(entityName, operationByEntityId);
    }
    return operationByEntityId;
  }
  private getAllPendingOperationsAsArray(): Array<Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> {
    const allPendingOperationsArray: Array<Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>> = [];
    const operationByEntityIds = Array.from(this.pendingOperations.values());
    for (const operationByEntityId of operationByEntityIds) {
      for (const operation of operationByEntityId.values()) {
        allPendingOperationsArray.push(operation);
      }
    }
    return allPendingOperationsArray;
  }
  private assertInitialized(): void {
    if (!this.initialized) {
      throw new Error("can't use the EntityStorage because it isn't initialized");
    }
  }
}
type OperationsByEntityName<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = Map<TSynchronizationEnvironmentTypes['CommonSynchronizationEnvironmentTypes']['EntityName'], OperationByEntityId<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>;
type OperationByEntityId<TSynchronizationEnvironmentTypes extends AnySynchronizationEnvironmentTypesConstraint, TEntityTypesByEntityName extends EntityManagerEntityTypesByEntityName<TSynchronizationEnvironmentTypes>> = Map<EntityManagerBaseEntity['id'], Operation<TSynchronizationEnvironmentTypes, TEntityTypesByEntityName>>;